import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from '@env/environment';
import { PasswordChangeRequest, PasswordChangeResult } from '../models/password-change.model';
import { UserAvatar, UserProfile } from '../models/user-profile.model';

@Injectable({ providedIn: 'root' })
export class MyAccountService {
  private readonly http = inject(HttpClient);

  /** Full URLs built from the environment base URL (which already ends with `api/v1/`). */
  private readonly epProfile = `${environment.apiBaseUrl}user/profile`;
  private readonly epAvatar = `${environment.apiBaseUrl}user/avatar`;
  private readonly epPassword = `${environment.apiBaseUrl}user/password`;

  /** Loads the profile of the authenticated user. The interceptor attaches the bearer token. */
  getProfile(): Observable<UserProfile> {
    return this.http.get<unknown>(this.epProfile).pipe(map((body) => this.toUserProfile(body)));
  }

  /**
   * Downloads the profile picture as a blob.
   *
   * The bucket is private and has no public URL: this route is the only way in
   * and it requires the same bearer token as the rest of the API. That rules out
   * `<img [src]="avatar.url">` — the tag sends no `Authorization` header, so the
   * browser would fetch the URL anonymously and get a `401`.
   *
   * Caching is left to the HTTP layer: the response carries `ETag` and
   * `Cache-Control: private, max-age=86400`, so the browser replays it from its
   * own cache and revalidates with `If-None-Match` (`304`) by itself.
   */
  getAvatar(): Observable<Blob> {
    return this.http.get(this.epAvatar, {
      headers: { Accept: 'image/*' },
      responseType: 'blob',
    });
  }

  /**
   * Sends a new profile picture as `multipart/form-data` under the `photo` field
   * and emits the picture the API stored (`url` + `version`).
   *
   * `Content-Type` is left unset on purpose: the browser has to write it itself
   * so the multipart boundary goes with it — `ApiHeadersInterceptor` keeps out of
   * the way for `FormData` bodies.
   *
   * A rejected file comes back as `422` on `photo` (not an image, too large, or
   * smaller than 64x64), with a message ready to display.
   */
  uploadAvatar(file: File): Observable<UserAvatar> {
    const body = new FormData();
    body.append('photo', file, file.name);

    return this.http
      .post<unknown>(this.epAvatar, body)
      .pipe(map((response) => this.toUserAvatar(response)));
  }

  /**
   * Drops the profile picture of the authenticated user.
   *
   * The route always answers `200` with `avatar: null` — including when the
   * account had no picture to begin with — so there is nothing to read out of
   * the body: the answer landing *is* the account having no picture. The stream
   * therefore emits nothing but completion.
   */
  removeAvatar(): Observable<void> {
    return this.http.delete<unknown>(this.epAvatar).pipe(map(() => undefined));
  }

  /**
   * Changes the password of the authenticated user.
   *
   * The three fields travel exactly as the contract names them. The API owns the
   * checks: it is the one that knows whether `current_password` is right, and it
   * answers a `422` on the offending field — a message ready to display — when
   * it is not, when the new password breaks the policy, or when the confirmation
   * does not match.
   */
  changePassword(payload: PasswordChangeRequest): Observable<PasswordChangeResult> {
    return this.http
      .patch<unknown>(this.epPassword, payload)
      .pipe(map((body) => this.toPasswordChangeResult(body)));
  }

  /**
   * Normalizes the profile payload, accepting a bare resource or a `{ data }` /
   * `{ user }` / `{ profile }` wrapper — the envelope is not uniform across the
   * API (compare `analyses/{id}` with `admin/users/{id}`).
   */
  private toUserProfile(body: unknown): UserProfile {
    if (body === null || typeof body !== 'object') {
      throw new Error('Unexpected empty body from the profile endpoint.');
    }

    const record = body as Record<string, unknown>;
    const wrapped = record['data'] ?? record['user'] ?? record['profile'];
    const resource = wrapped !== null && typeof wrapped === 'object' ? wrapped : record;

    return resource as UserProfile;
  }

  /**
   * Reads the confirmation sentence out of the answer. Success is the status
   * code, not the body: an empty `204` is as valid as a `{ message }`, so a body
   * without one yields `null` instead of failing.
   */
  private toPasswordChangeResult(body: unknown): PasswordChangeResult {
    if (body === null || typeof body !== 'object') {
      return { message: null };
    }

    const record = body as Record<string, unknown>;
    const wrapped = record['data'];
    const resource = (wrapped !== null && typeof wrapped === 'object' ? wrapped : record) as Record<
      string,
      unknown
    >;

    const message = resource['message'] ?? record['message'];

    return { message: typeof message === 'string' ? message : null };
  }

  /**
   * Reads the stored picture out of the upload answer: `{ avatar }` — wrapped in
   * the usual envelope or not — or the resource on its own.
   */
  private toUserAvatar(body: unknown): UserAvatar {
    if (body === null || typeof body !== 'object') {
      throw new Error('Unexpected empty body from the avatar upload endpoint.');
    }

    const record = body as Record<string, unknown>;
    const wrapped = record['data'] ?? record['user'] ?? record['profile'];
    const resource = (wrapped !== null && typeof wrapped === 'object' ? wrapped : record) as Record<
      string,
      unknown
    >;

    const nested = resource['avatar'];
    const avatar = (nested !== null && typeof nested === 'object' ? nested : resource) as Record<
      string,
      unknown
    >;

    const url = avatar['url'];
    const version = avatar['version'];

    if (typeof url !== 'string' || (typeof version !== 'string' && typeof version !== 'number')) {
      throw new Error('Avatar upload answered without a usable picture.');
    }

    // Versions travel as strings in the profile payload; a numeric one is still
    // the same token, so it is normalized instead of rejected.
    return { url, version: String(version) };
  }
}
